import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DeleteConfirmationModal from "../../../components/commonComponent/DeleteConfirmationModal";
import Pagination from "../../../components/commonComponent/Pagination";
import Search from "../../../components/commonComponent/Search";
import Table from "../../../components/commonComponent/Table";
import ViewModal from "../../../components/commonComponent/ViewModal";
import {
  FIELDS,
  ITEMS_PER_PAGE,
  TOAST_MESSAGES,
} from "../../../constants/SiteConstants";
import useModal from "../../../hooks/useModal";
import "../../../styles/SolarStation.css";
import { deleteData, fetchData } from "../../../utils/utils";
import useLoader from "../../../hooks/useLoader";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const API_URL = process.env.REACT_APP_API_URL;

const columns = [
  { header: FIELDS.NAME, accessor: "name" },
  { header: FIELDS.LOCATION, accessor: "location" },
  { header: FIELDS.PHONE, accessor: "phone" },
  { header: FIELDS.CITY, accessor: "city" },
];

const Vendor = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [cityFilter, setCityFilter] = useState("");
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [allCities, setAllCities] = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedFields, setSelectedFields] = useState([]);

  // 🆕 full dataset for export (independent of paginated table)
  const [allVendors, setAllVendors] = useState([]);
  const [exportLoading, setExportLoading] = useState(false); // 🆕

  const { loading, showLoader, hideLoader } = useLoader();

  const {
    showDeleteModal,
    showViewModal,
    selectedItem,
    handleOpenDeleteModal,
    handleCloseDeleteModal,
    handleOpenViewModal,
    handleCloseViewModal,
  } = useModal();

  const navigate = useNavigate();

  // ✅ Paginated data for table (unchanged)
  useEffect(() => {
    fetchData(
      `${API_URL}/vendor/getAllVendors`,
      setData,
      setError,
      setFilteredData,
      currentPage,
      searchTerm,
      cityFilter,
      showLoader,
      hideLoader,
      setTotalPages
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm, cityFilter]);

  // ✅ All cities
  useEffect(() => {
    fetch(`${API_URL}/stateWithCities/c/getall`)
      .then((res) => res.json())
      .then((res) => setAllCities(res || []))
      .catch((err) => console.error("Error fetching cities:", err));
  }, []);

  // ✅ Client-side filter for table view (unchanged)
  useEffect(() => {
    const filtered = data.filter((item) => {
      const matchesSearch =
        (item.name &&
          item.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.phone &&
          String(item.phone).toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.location &&
          item.location.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.city &&
          item.city.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesCity = cityFilter
        ? item.city?.trim().toLowerCase() === cityFilter.trim().toLowerCase()
        : true;

      return matchesSearch && matchesCity;
    });
    setFilteredData(filtered);
  }, [data, searchTerm, cityFilter]);

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
    setCurrentPage(1);
  };

  const handleCityFilter = (event) => {
    setCityFilter(event.target.value);
    setCurrentPage(1);
  };

  const handleDeleteConfirm = () => {
    deleteData(`${API_URL}/vendor/deleteVendor`, selectedItem._id, setError)
      .then(() => {
        setData((prevData) =>
          prevData.filter((item) => item._id !== selectedItem._id)
        );
        toast.success(TOAST_MESSAGES.DELETED, { autoClose: 1000 });
        fetchData(
          `${API_URL}/vendor/getAllVendors`,
          setData,
          setError,
          setFilteredData,
          currentPage,
          searchTerm,
          showLoader,
          hideLoader
        );
      })
      .catch((error) => {
        setError(error);
        toast.error(TOAST_MESSAGES.ERROR_DELETE, { autoClose: 1000 });
      });
    handleCloseDeleteModal();
  };

  const fieldsToShow = {
    Name: "name",
    "Brief Description": "briefDescription",
    Description: "description",
    Image: "image",
  };

  const allFields = [
    "_id",
    "name",
    "location",
    "city",
    "zipcode",
    "phone",
    "email",
    "workOnCompany",
    "tags",
    "ranking",
    "sponsored",
    "verified",
    "ownerName",
    "image",
    "lat",
    "lng",
    "slug",
    "branches",
    "reviewCount",
    "description",
    "approvedByGovernment",
    "approvedBySolarStation",
    "isActive",
    "state",
    "userId",
  ];

  // Toggle field checkbox
  const handleFieldToggle = (field) => {
    setSelectedFields((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]
    );
  };

  // ----------------------------
  // 🆕 Helpers for export fetch
  // ----------------------------
  const extractVendors = (json) => {
    if (Array.isArray(json)) return json;
    if (Array.isArray(json?.data)) return json.data;
    if (Array.isArray(json?.vendors)) return json.vendors;
    if (Array.isArray(json?.results)) return json.results;
    // sometimes APIs wrap inside {data:{docs:[], totalPages:..}}
    if (Array.isArray(json?.data?.docs)) return json.data.docs;
    return [];
  };

  const extractTotalPages = (json) => {
    if (typeof json?.totalPages === "number") return json.totalPages;
    if (typeof json?.pagination?.totalPages === "number")
      return json.pagination.totalPages;
    // fallbacks
    const total = json?.total ?? json?.count ?? json?.data?.total ?? undefined;
    const limit = json?.limit ?? json?.perPage ?? json?.data?.limit ?? ITEMS_PER_PAGE;
    if (typeof total === "number" && typeof limit === "number" && limit > 0) {
      return Math.max(1, Math.ceil(total / limit));
    }
    return 1;
  };

  // ----------------------------
  // 🆕 Separate useEffect for EXPORT
  //    -> fetch ALL pages and merge
  // ----------------------------
  const fetchAllVendorsForExport = useCallback(async () => {
    setExportLoading(true);
    try {
      // 1) Hit page 1 to discover totalPages
      const firstUrl = `${API_URL}/vendor/getAllVendors?page=1&limit=${ITEMS_PER_PAGE}`;
      const firstRes = await fetch(firstUrl);
      const firstJson = await firstRes.json();

      let vendors = extractVendors(firstJson);
      const total = extractTotalPages(firstJson);

      // 2) Parallel fetch remaining pages (if any)
      if (total > 1) {
        const promises = [];
        for (let p = 2; p <= total; p++) {
          const url = `${API_URL}/vendor/getAllVendors?page=${p}&limit=${ITEMS_PER_PAGE}`;
          promises.push(fetch(url).then((r) => r.json()));
        }
        const pages = await Promise.all(promises);
        pages.forEach((js) => {
          vendors = vendors.concat(extractVendors(js));
        });
      }

      setAllVendors(vendors);
    } catch (err) {
      console.error("Error fetching all vendors for export:", err);
      toast.error("Failed to fetch full vendor list for export");
      setAllVendors([]); // safe fallback
    } finally {
      setExportLoading(false);
    }
  }, [API_URL]);

  // 🆕 Trigger this ONLY when export modal opens.
  // NOTE: This fetches the *entire dataset* regardless of current table filters.
  useEffect(() => {
    if (showExportModal) {
      fetchAllVendorsForExport();
    }
  }, [showExportModal, fetchAllVendorsForExport]);

  // ✅ Excel export using ALL vendors
const handleExport = () => {
  if (selectedFields.length === 0) {
    toast.error("Please select at least one field!");
    return;
  }

  // ✅ Agar city select kiya hai to filter karke export karo
  const vendorsToExport = cityFilter
    ? allVendors.filter(
        (vendor) =>
          vendor.city?.trim().toLowerCase() === cityFilter.trim().toLowerCase()
      )
    : allVendors;

  if (vendorsToExport.length === 0) {
    toast.error("No vendors found for the selected city!");
    return;
  }

  const exportData = vendorsToExport.map((vendor) => {
    let obj = {};
    selectedFields.forEach((field) => {
      obj[field] = vendor[field] !== undefined ? vendor[field] : "";
    });
    return obj;
  });

  const ws = XLSX.utils.json_to_sheet(exportData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Vendors");

  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  saveAs(
    new Blob([excelBuffer], { type: "application/octet-stream" }),
    cityFilter ? `vendors_${cityFilter}.xlsx` : "vendors.xlsx"
  );

  setShowExportModal(false);
};


  return (
    <div>
      <h2 className="capitalize font-bold text-[24px] mb-4">Vendors List</h2>

      {/* Topbar */}
      <div className="flex justify-between items-center shadow-md px-8 py-[10px] rounded-md bg-[#fff] text-base mb-4">
        <div className="flex items-center">
          <div className="search-filter-container">
            <Search
              searchFields={["First Name", "Last Name", "Email", "City"]}
              onSearch={handleSearch}
            />
          </div>
          <div className="ml-4">
            <select
              className="select-input"
              value={cityFilter}
              onChange={handleCityFilter}
            >
              <option value="">All Cities</option>
              {allCities.map((city, index) => (
                <option key={city._id || index} value={city.name}>
                  {city.name}
                </option>
              ))}
            </select>
          </div>

          {/* Export Button */}
          <button
            onClick={() => setShowExportModal(true)}
            className="ml-4 text-white px-4 py-2 rounded bg-[#1C2434] hover:bg-opacity-90"
          >
            Export Vendor Data
          </button>
        </div>

        <Link
          to="/Vendors/add"
          className="inline-flex items-center justify-center rounded-md border border-white px-4 py-1.5 text-center font-medium text-white bg-[#1C2434] hover:bg-opacity-90"
        >
          Add Vendor
        </Link>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black bg-opacity-40"
            onClick={() => setShowExportModal(false)}
          ></div>

          {/* Modal */}
          <div className="relative bg-white rounded-lg shadow-xl p-6 w-[600px] max-h-[80vh] overflow-y-auto z-10">
            <h3 className="text-xl font-semibold mb-2 text-gray-800">
              Select Fields to Export
            </h3>

            {/* 🆕 status line */}
            <p className="text-sm text-gray-600 mb-4">
              {exportLoading
                ? "Fetching full vendor list…"
                : `Ready: ${allVendors.length} vendors`}
            </p>

            <div className="grid grid-cols-2 gap-3 mb-6">
              {allFields.map((field) => (
                <label
                  key={field}
                  className="flex items-center gap-2 text-gray-700 text-sm"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-green-600"
                    checked={selectedFields.includes(field)}
                    onChange={() => handleFieldToggle(field)}
                  />
                  {field}
                </label>
              ))}
            </div>

            <div className="flex justify-end gap-3">
              <button
                className="bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-500"
                onClick={() => setShowExportModal(false)}
                disabled={exportLoading}
              >
                Cancel
              </button>
              <button
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-60"
                onClick={handleExport}
                disabled={exportLoading || allVendors.length === 0}
              >
                {exportLoading ? "Preparing…" : "Export"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="table-container">
        <Table
          columns={columns}
          data={filteredData}
          showIndex={true}
          currentPage={currentPage}
          itemsPerPage={ITEMS_PER_PAGE}
          onDelete={handleOpenDeleteModal}
          onUpdate={(item) => navigate(`/vendors/edit/${item._id}`)}
          onView={handleOpenViewModal}
          actions={{ showView: true, showEdit: true, showDelete: true }}
          loading={loading}
        />
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>

      {/* Delete Modal */}
      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        onConfirm={handleDeleteConfirm}
      />

      {/* View Modal */}
      <ViewModal
        show={showViewModal}
        onHide={handleCloseViewModal}
        data={selectedItem}
        fields={fieldsToShow}
      />
    </div>
  );
};

export default Vendor;



